import { ensureUnsubscribeFooter } from './beefree-document';
import { blocksToHtml, blocksToPlainText, EDITOR_META, htmlToPlainText } from './template';
import type { EmailBlock, EmailTemplate, TemplateDraft } from './types';

/**
 * O que o editor entrega vira um rascunho fechado antes de ir para a API (ou
 * para a biblioteca local da plataforma): HTML com o rodapé de descadastro,
 * texto simples derivado, blocos do editor antigo quando é o caso.
 */
export function prepareTemplateDraft(draft: TemplateDraft): TemplateDraft {
  const usesBeefree = draft.editor === 'beefree';
  const blocks: EmailBlock[] =
    draft.editor === 'rich-text'
      ? [{ id: 'mensagem', kind: 'text', label: 'Texto', text: draft.plainText }]
      : draft.blocks.map((block) => ({ ...block }));
  const html = usesBeefree ? ensureUnsubscribeFooter(draft.html) : draft.editor === 'html' ? draft.html : blocksToHtml(blocks);
  const plainText = usesBeefree
    ? htmlToPlainText(html)
    : draft.editor === 'html' || draft.editor === 'rich-text'
      ? draft.plainText
      : blocksToPlainText(blocks);
  return {
    ...draft,
    name: draft.name.trim().slice(0, 50),
    description: draft.description.trim(),
    subject: draft.subject.trim(),
    previewText: draft.previewText.trim(),
    blocks,
    html,
    plainText,
    tags: draft.tags.map((tag) => tag.trim()).filter(Boolean).slice(0, 5),
    domain: draft.domain.trim(),
  };
}

/**
 * Biblioteca em memória (escopo da plataforma, até o backend ter template com
 * `scope: 'platform'`). Mesmas regras da API: `sandbox` não muda, nasce cópia.
 */
export function buildLocalTemplate(
  prepared: TemplateDraft,
  source: EmailTemplate | undefined,
  now = new Date().toISOString(),
): { template: EmailTemplate; forked: boolean } {
  const forked = source?.origin === 'sandbox';
  const template: EmailTemplate = {
    id: !source || forked ? `tpl-local-${Date.now()}` : source.id,
    name: prepared.name,
    category: prepared.category,
    description: prepared.description || `${EDITOR_META[prepared.editor].label} · salvo nesta sessão`,
    subject: prepared.subject,
    previewText: prepared.previewText,
    editor: prepared.editor,
    origin: 'local',
    blocks: prepared.blocks,
    html: prepared.html,
    beefreeJson: prepared.beefreeJson,
    plainText: prepared.plainText,
    tags: prepared.tags,
    domain: prepared.domain || source?.domain || '',
    updatedAt: now,
    appliesTo: prepared.appliesTo ?? [],
    variantOf: prepared.variantOf,
  };
  return { template, forked };
}

export function duplicateLocalTemplate(source: EmailTemplate, now = new Date().toISOString()): EmailTemplate {
  return {
    ...structuredClone(source),
    id: `tpl-local-${Date.now()}`,
    name: `Cópia de ${source.name}`.slice(0, 50),
    origin: 'local',
    updatedAt: now,
  };
}
